// Copyright (c) StrangeBee 2024

import PreviewButton from '@Atoms/Buttons/PreviewButton/PreviewButton';
import THTag from '@Atoms/THTag/THTag';
import client from '@Client';
import { HTMLColors } from '@Constants/colors';
import { useLocalMessage } from '@Helpers/locale';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { useDrawer } from '@Hooks/useDrawer';
import { THDatePicker } from '@Molecules/Inputs/THDatePicker/THDatePicker';
import { THEditable } from '@Molecules/Inputs/THEditable/THEditable';
import { IsIoc } from '@Organisms/IsIoc/IsIoc';
import { IsSighted } from '@Organisms/IsSighted/IsSighted';
import { computeEventList } from '@Organisms/Observable/eventList';
import { ObservableDrawer } from '@Organisms/Observable/ObservableDrawer/ObservableDrawer';
import { DisplayEventProps } from '@Organisms/Timeline/DisplayEvent';
import { EventCard } from '@Organisms/Timeline/EventCard';
import React, { ReactElement } from 'react';
import { useParams } from 'react-router';
import { ObservableOutput } from 'thehive-sdk';

import './ObservableContent.scss';

export const ObservableContent = (props: DisplayEventProps): ReactElement => {
    const { caseId } = useParams<{ caseId: string }>();
    const i = useLocalMessage();
    const data = props.currentEvent.details?.observable;
    const { refetch } = props;
    const { visible, setVisible } = useDrawer<ObservableOutput>({});

    const { data: obsData, dataType, attachment, sightedAt = 0, ioc = false, sighted = false } = data || {};

    return (
        <EventCard {...props}>
            <div className="observable-content card__content">
                <div className="observable-content__header card__header">
                    <div className="observable-content__title">
                        <THTag value={dataType} outline full color={HTMLColors.BLUE} />
                        {obsData || attachment?.name}
                    </div>
                    <div className="observable-content__title">
                        {ioc && <IsIoc value={ioc} />}
                        {sighted && <IsSighted value={sighted} />}
                    </div>
                </div>

                {!!sightedAt && (
                    <THEditable
                        name="sightedAt"
                        className="card-info__date"
                        initialValues={{ sightedAt }}
                        label={i('common.sighted-at')}
                    >
                        <THDatePicker disabled borderless />
                    </THEditable>
                )}

                {data && (
                    <div className="card__preview">
                        <PreviewButton onClick={() => setVisible(true)} />
                    </div>
                )}

                {visible && (
                    <ObservableDrawer
                        data={data}
                        onVisibleChange={setVisible}
                        visible={visible}
                        caseId={caseId}
                        eventList={computeEventList({
                            controller: client.ObservableController,
                            locale: i,
                            onSuccess: (_, message) => {
                                message && openMessage('success', message);
                                refetch?.();
                            },
                            onError: handleNotificationError,
                        })}
                    />
                )}
            </div>
        </EventCard>
    );
};
